import {
  Button,
  Image,
  Input,
  message,
  Modal,
  Skeleton,
  Spin,
  Table,
  Tag,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import moment from "moment";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectBaseUrl, selectProdTestUrl } from "../../redux/slices/envSlice";
import { labAccessionStart, labSampleCollect, rejectPouchItem } from "../../endpoints/API";

const formatDateTime = (text) => {
  if (text) {
    const formattedDate = moment(text).format("DD-MM-YYYY"); // Date format
    const formattedTime = moment(text).format("h:mm A"); // Time format (e.g., 3:15 PM)
    return (
      <div>
        <Tag color="green">{formattedDate}</Tag>
        <Tag color="orange">{formattedTime}</Tag>
      </div>
    );
  }
  return "";
};

const ScanPouchOrContainer = () => {
  const user = useSelector((state) => state.user.user);
  const [barcode, setBarcode] = useState("");
  const [pouchData, setPouchData] = useState(null);
  const baseUrl = useSelector(selectBaseUrl);
  const prodTestUrl = useSelector(selectProdTestUrl);
  const [selectedPouchItemBarcode, setSelectedPouchItemBarcode] =
    useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOrderNumberPatientID, setIsOrderNumberPatientID] = useState(null);
  const [isAccessionData, setIsAccessionData] = useState(null);

  console.log(pouchData);

  let isAccessionBool = false;

  // Logic to check and set isAccession
  isAccessionData?.forEach((item) => {
    if (
      item.ordernumber_PatientId === isOrderNumberPatientID &&
      (item.status === "partial" || item.status === "complete")
    ) {
      isAccessionBool = true;
    }
  });

  console.log({ isAccessionBool });

  const handleModalOk = async () => {
    if (!rejectionReason.trim()) {
      message.error("Please provide a reason for rejection.");
      return;
    }

    const payload = {
      pouch_item_id: selectedPouchItemBarcode,
      reject: true,
      reason: rejectionReason,
    };

    setLoading(true); // Start loading spinner
    try {
      const response = await rejectPouchItem(payload)

      if (response.statusText=="OK") {
        message.success("Rejection reason saved successfully.");
        setIsModalVisible(false);
        setRejectionReason("");
        setSelectedPouchItemBarcode(null);
        handleCollect();
      } else {
        const errorData = response?.data;
        message.error(
          errorData?.message ||
            "Failed to save rejection reason. Please try again."
        );
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"An error occurred. Please try again.");
    } finally {
      setLoading(false); // Stop loading spinner
    }
  };

  const handleCollect = async () => {
    if (!barcode.trim()) {
      message.error("Please input a valid barcode.");
      return;
    }

    const payload = {
      barcode,
      userId: user?.id,
    };

    try {
      const response = await labSampleCollect(payload)

      if (response.statusText =="OK") {
        const result = response?.data;

        message.success("Sample collected successfully!");
        setPouchData(result.pouch_data); 
        setIsAccessionData(result?.accession_data);
      } else {
        message.error("Barcode does not exist.");
      }
    } catch (error) {
      message.error(error?.response?.data?.error||"Barcode does not exist.");
    }
  };

  const handleReject = (pouchItemBarcode) => {
    setSelectedPouchItemBarcode(pouchItemBarcode); // Set the selected pouch item barcode
    setIsModalVisible(true); // Show the rejection modal
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setRejectionReason("");
    setSelectedPouchItemBarcode(null);
  };

  const pouchItemPatientDetailsColumns = [
    {
      title: "Order ID",
      dataIndex: "orders",
      key: "orders",
      render: (_, record) => {
        const orderNumber = record.orders?.[0]?.order_number; // Accessing order_number from orders array
        const familyMemberId = record.family_member?.id; // Accessing family member id
        return <Tag>{`${orderNumber}-${familyMemberId}`}</Tag>;
      },
    },
    {
      title: "Patient Name",
      dataIndex: "family_member",
      key: "family_member",
      render: (familyMember) => familyMember?.name || "N/A", // Handles cases where family_member might be null or undefined
    },
  ];

  const pouchItemColumns = [
    {
      title: "Pouch Item Image",
      dataIndex: "container",
      key: "container",
      render: (container) => {
        if (container && container.image) {
          return (
            <Image
              src={`${prodTestUrl ? prodTestUrl : baseUrl}${container.image}`}
              alt="Container"
              width={50}
              height={50}
            />
          );
        }
        return <Tag color="red">No Image</Tag>;
      },
    },
    {
      title: "Pouch Item Barcode",
      dataIndex: "pouch_item_barcode",
      key: "pouch_item_barcode",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Time Stamp",
      dataIndex: "time_stamp",
      key: "time_stamp",
      render: (text) => {
        if (text) {
          return moment(text).format("DD-MM-YYYY"); // Format using moment.js for start date-time
        }
        return "";
      },
    },
    {
      title: "Lab User Scan Time",
      dataIndex: "lab_user_scan_time",
      key: "lab_user_scan_time",
      render: (text) => {
        if (text) {
          const formattedDate = moment(text).format("DD-MM-YYYY"); // Format date as '31-12-2024'
          const formattedTime = moment(text).format("h:mm A"); // Format time as '3:15 PM'
          return (
            <div>
              <Tag color="green">{formattedDate}</Tag>
              <Tag color="orange">{formattedTime}</Tag>
            </div>
          );
        }
        return "";
      },
    },
    {
      title: "Action",
      dataIndex: "reject",
      key: "reject",
      render: (reject, record) => {
        if (reject) {
          return <div>{record.reason || "No reason provided"}</div>;
        }
        return (
          <Button
            type="danger"
            ghost
            onClick={() => handleReject(record.pouch_item_barcode)}
          >
            Reject
          </Button>
        );
      },
    },
  ];

  // Define columns for the main pouch data
  const pouchColumns = [
    {
      title: "Pouch Barcode",
      dataIndex: "pouch_barcode",
      key: "pouch_barcode",
      render: (text) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Start Date-Time",
      dataIndex: "start_date_time",
      key: "start_date_time",
      render: (text) => formatDateTime(text),
    },
    {
      title: "End Date-Time",
      dataIndex: "end_date_time",
      key: "end_date_time",
      render: (text) => formatDateTime(text),
    },
    {
      title: "Lab User Scan Time",
      dataIndex: "lab_user_scan_time",
      key: "lab_user_scan_time",
      render: (text) => {
        if (text) {
          const formattedDate = moment(text).format("DD-MM-YYYY"); // Format date as '31-12-2024'
          const formattedTime = moment(text).format("h:mm A"); // Format time as '3:15 PM'
          return (
            <div>
              <Tag color="green">{formattedDate}</Tag>
              <Tag color="orange">{formattedTime}</Tag>
            </div>
          );
        }
        return "";
      },
    },
  ];

  // Define columns for the order items data
  const orderItemColumns = [
    {
      title: "Order ID",
      dataIndex: "orders",
      key: "orders",
      render: (_, record) => {
        const orderNumber = record.orders?.[0]?.order_number; // Accessing order_number from orders array
        const familyMemberId = record.family_member?.id; // Accessing family member id
        setIsOrderNumberPatientID(`${orderNumber}-${familyMemberId}`);
        return <Tag>{`${orderNumber}-${familyMemberId}`}</Tag>;
      },
    },
    {
      title: "Patient Name",
      dataIndex: "family_member", // Use family_member as dataIndex
      key: "family_member",
      render: (record) => {
        return record?.name ? record?.name : "No Name";
      },
    },
    { title: "Test", dataIndex: "test", key: "test" },
    { title: "Package", dataIndex: "package", key: "package" },
    {
      title: "Sample Collect Date-Time",
      dataIndex: "sample_collect_datetime",
      key: "sample_collect_datetime",
      render: (text) => formatDateTime(text),
    },
    {
      title: "Sample Handover Date-Time",
      dataIndex: "sample_handover_datetime",
      key: "sample_handover_datetime",
      render: (text) => formatDateTime(text),
    },
  ];

  // Define columns for the order items data
  const orderItemBarcodeColumns = [
    {
      title: "Pouch Item Image",
      dataIndex: "container",
      key: "container",
      render: (container) => {
        if (container && container.image) {
          return (
            <Image
              src={`${prodTestUrl ? prodTestUrl : baseUrl}secured_media_access${container.image}`}
              alt="Container"
              width={50}
              height={50}
            />
          );
        }
        return <Tag color="red">No Image</Tag>;
      },
    },
    {
      title: "Pouch Item Barcode",
      dataIndex: "barcode",
      key: "barcode",
      render: (text, record) => {
        if (!record.lab_user_scan_time) {
          return <Skeleton.Input style={{ width: 100 }} active={true} />;
        }
        return <Tag color="green">{text}</Tag>;
      },
    },
    {
      title: "Lab User Scan Time",
      dataIndex: "lab_user_scan_time",
      key: "lab_user_scan_time",
      render: (text) => {
        if (text) {
          const formattedDate = moment(text).format("DD-MM-YYYY"); // Format date as '31-12-2024'
          const formattedTime = moment(text).format("h:mm A"); // Format time as '3:15 PM'
          return (
            <div>
              <Tag color="green">{formattedDate}</Tag>
              <Tag color="orange">{formattedTime}</Tag>
            </div>
          );
        }
        return "";
      },
    },
    {
      title: "Reject/Reject Reason/Pending",
      dataIndex: "reject",
      key: "reject",
      render: (_, record) => (
        <>
          {record?.reason ? (
            record.reason // Display the rejection reason if it exists
          ) : (
            <>
              {!record.lab_user_scan_time ? (
                <Tag>Scan Pending</Tag>
              ) : isAccessionBool === false ? (
                <Button
                  type="primary"
                  ghost
                  onClick={() => handleReject(record.barcode)} // Pass pouch item barcode
                >
                  Reject
                </Button>
              ) : (
                <Tag color="green">Accession Started</Tag>
              )}
            </>
          )}
        </>
      ),
    },
  ];

  const handleAccession = async (status) => {
    if (!pouchData || !user?.id) {
      message.error("Required data is missing.");
      return;
    }

    const orders = pouchData?.order_items?.map((item) => {
      const orderId = item?.orders?.map((orderid) => orderid?.order_id);
      const familyMemberId = item?.family_member?.id;
      const orderNumber = item?.orders?.[0]?.order_number;

      return {
        orderId,
        familyMemberId,
        orderNumberFamilyMemberId: `${orderNumber}-${familyMemberId}`,
      };
    });

    const payload = {
      labUserId: user.id, // Lab user ID
      status, // Accession status: "partial" or "complete"
      orders, // Orders with family member and formatted IDs
    };

    setLoading(true);
    try {
      const response = await labAccessionStart(payload)

      if (response.statusText=="OK") {
        message.success(
          status === "partial"
            ? "Partial accession started successfully."
            : "Accession completed successfully."
        );
        handleCollect();
      } else {
        const errorData = response?.data;
        message.error(
          errorData?.message || "Failed to start accession. Please try again."
        );
      }
    } catch (error) {
      message.error(error?.response?.data?.error ||"An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Spin spinning={loading} tip="Processing...">
      <div>
        <div className="flex gap-5">
          <div className="p-1 font-semibold">Scan Barcode:</div>
          <Input
            placeholder="Input pouch/container barcode"
            autoSize={{ minRows: 1, maxRows: 5 }}
            className="w-[60%]"
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === "Enter") {
                handleCollect();
              }
            }}
          />
          <Button type="primary" onClick={handleCollect}>
            Collect
          </Button>
        </div>

        {/* Display pouch data */}
        {pouchData ? (
          <div className="mt-[2rem]">
            {/* Display order items if they exist */}
            {pouchData?.order_items && pouchData?.order_items?.length > 0 && (
              <div className="mt-4">
                <h3 className="font-semibold p-2 text-lg">Patient Details</h3>
                <Table
                  dataSource={pouchData?.order_items} // Order items
                  columns={orderItemColumns}
                  rowKey="order_item_id"
                  pagination={false} // Disable pagination for order items table
                  bordered
                />
              </div>
            )}
            <h3 className="font-semibold p-2 text-lg mt-[1rem]">
              Pouch Details
            </h3>
            <Table
              dataSource={[pouchData]} // Pouch data as a single item in the array
              columns={pouchColumns}
              rowKey="pouch_barcode"
              pagination={false} // Disable pagination for single-row data
              bordered
            />
            {pouchData?.pouch_items && (
              <div className="mt-4">
                <h3 className="font-semibold p-2 text-lg mt-[1rem]">
                  Pouch Item
                </h3>
                <Table
                  dataSource={pouchData?.pouch_items}
                  columns={orderItemBarcodeColumns}
                  rowKey="pouch_item_id"
                  pagination={false} // Disable pagination for order items table
                  bordered
                />
              </div>
            )}
            {pouchData?.pouch_items?.length > 0 && (
              <div className="flex justify-end">
                {isAccessionBool === false ? (
                  <>
                    {pouchData.pouch_items.filter(
                      (item) => item.lab_user_scan_time
                    ).length === pouchData.pouch_items.length && (
                      <Button
                        type="primary"
                        className="mt-[1rem]"
                        onClick={() => handleAccession("complete")}
                      >
                        Start Accession
                      </Button>
                    )}
                    {pouchData.pouch_items.filter(
                      (item) => item.lab_user_scan_time
                    ).length > 0 &&
                      pouchData.pouch_items.filter(
                        (item) => item.lab_user_scan_time
                      ).length !== pouchData.pouch_items.length && (
                        <Button
                          type="primary"
                          className="mt-[1rem]"
                          onClick={() => handleAccession("partial")}
                        >
                          Start Partial Accession
                        </Button>
                      )}
                  </>
                ) : (
                  ""
                )}
              </div>
            )}
          </div>
        ) : (
          ""
        )}

        {/* {pouchData?.pouch_item_barcode ? (
          <div>
            <Table
              columns={pouchItemPatientDetailsColumns}
              dataSource={[pouchData]}
              pagination={false}
            />
            <Table
              className="mt-[1rem]"
              columns={pouchItemColumns}
              dataSource={[pouchData]}
            />
          </div>
        ) : (
          ""
        )} */}

        <Modal
          title="Reject Pouch Item"
          visible={isModalVisible}
          onOk={handleModalOk}
          onCancel={handleModalCancel}
        >
          <div>
            <label
              htmlFor="rejectionReason"
              className="block font-semibold mb-2"
            >
              Reason for Rejection:
            </label>
            <TextArea
              id="rejectionReason"
              placeholder="Enter reason for rejection"
              autoSize={{ minRows: 3, maxRows: 5 }}
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
            />
          </div>
        </Modal>
      </div>
    </Spin>
  );
};

export default ScanPouchOrContainer;
